import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconCheck, IconCloudOff, IconPlayerPlay } from '@tabler/icons-react';
import { PROCEDURES_RUN_STATUS, PROCEDURES_RUN_STEP_STATUS } from '@actschurch/ccms-sdk';
import { Callout, EmptyState, LoadingState, SectionCard, StickyActionBar, SummaryRow } from '@/components/ui';
import { formatDuration, stampToDate } from '@/lib/format';
import { usePortalMode, useProcedureView, useRunView, type PortalMode } from '@/lib/portalApi';
import { buildRunnerModel } from '@/lib/runMerge';
import { clearInProgress, getRunRecord, type LocalRunRecord } from '@/lib/runStore';
import { useRunPending } from '@/lib/syncQueue';

/**
 * /p/:code/done/:runId: the finish screen. Time taken, steps done, problems reported and whether
 * everything has synced yet. "Done" goes back to the directory.
 */
export default function DonePage() {
  const { code = '', runId: runIdParam } = useParams();
  const runId = Number(runIdParam);
  const valid = Number.isFinite(runId) && runId > 0;
  const navigate = useNavigate();
  const { mode: sessionMode, isInitializing } = usePortalMode();
  const [record] = useState<LocalRunRecord | null>(() => (valid ? getRunRecord(runId) : null));
  const runMode: PortalMode | null = record?.mode ?? (sessionMode === 'member' && !isInitializing ? 'member' : null);
  const runQuery = useRunView(valid ? runId : null, runMode);
  const viewQuery = useProcedureView(code);
  const pending = useRunPending(valid ? runId : null);
  const run = runQuery.data ?? null;
  const view = viewQuery.data?.view;

  const model = useMemo(() => buildRunnerModel(view, run, pending), [view, run, pending]);

  useEffect(() => {
    if (valid && (record?.state === 'completed' || run?.statusID === PROCEDURES_RUN_STATUS.Completed)) clearInProgress(code, runId);
  }, [valid, record?.state, run?.statusID, code, runId]);

  if (isInitializing) return <div className="proc-main"><LoadingState variant="spinner" label="Loading" /></div>;
  if (!valid || !runMode) {
    return (
      <div className="proc-main">
        <EmptyState icon={<IconCheck size={28} />} title="This run isn't on this phone" action={<Button component={Link} to="/" fullWidth>All procedures</Button>} />
      </div>
    );
  }
  if ((!run && runQuery.fetchStatus === 'fetching') || (!view && viewQuery.fetchStatus === 'fetching')) {
    return <div className="proc-main"><LoadingState label="Adding it all up" count={1} /></div>;
  }

  const stillRunning =
    run?.statusID === PROCEDURES_RUN_STATUS.InProgress && !model.finishPending && record?.state !== 'completed';
  if (stillRunning) {
    return (
      <div className="proc-main">
        <EmptyState
          icon={<IconPlayerPlay size={28} />}
          title="This run isn't finished yet"
          action={<Button component={Link} to={`/p/${encodeURIComponent(code)}/run/${runId}`} fullWidth>Carry on</Button>}
        />
      </div>
    );
  }

  const started = stampToDate(run?.startedAt) ?? (record ? new Date(record.startedAtMs) : null);
  const ended =
    stampToDate(run?.completedAt) ?? (record?.finishedAtMs ? new Date(record.finishedAtMs) : null);
  const seconds = started && ended ? Math.max(0, (ended.getTime() - started.getTime()) / 1000) : null;
  const problems = model.steps.filter((s) => s.statusID === PROCEDURES_RUN_STEP_STATUS.Problem);
  const notTicked = model.steps.filter((s) => !s.statusID);
  const title = view?.title ?? run?.procedureTitle ?? record?.title ?? 'The procedure';
  const name = run?.runnerName ?? record?.runnerName ?? null;

  return (
    <div className="proc-main proc-hasbar">
      <div className="proc-pagebody">
      <div className="proc-success">
        <div className="check" aria-hidden="true"><IconCheck size={34} /></div>
        <h1 className="proc-title">{name ? `Thank you, ${name.split(' ')[0]}` : 'All done'}</h1>
        <p className="proc-lede">You finished {title}.</p>
      </div>

      {pending.length > 0 && (
        <Callout tone="warn" icon={<IconCloudOff size={20} />} title="Saved on this phone" live>
          It will sync by itself when you have signal. You can close this page.
        </Callout>
      )}

      <SectionCard title="Summary" flush>
        {seconds !== null && <SummaryRow k="Time taken" v={formatDuration(seconds)} />}
        <SummaryRow k="Steps done" v={`${model.done} of ${model.total}`} />
        {model.skipped > 0 && <SummaryRow k="Skipped" v={String(model.skipped)} />}
        <SummaryRow k="Problems reported" v={String(problems.length)} />
        {notTicked.length > 0 && <SummaryRow k="Not ticked off" v={String(notTicked.length)} />}
      </SectionCard>

      {problems.length > 0 && (
        <SectionCard title="Problems you reported" description="The team that looks after this procedure has been told.">
          <ul className="proc-problemlist">
            {problems.map((s) => (
              <li key={s.stepID}>
                <b>Step {s.number}: {s.title}</b>
                {s.note && <span>{s.note}</span>}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
      </div>

      <StickyActionBar>
        <Button size="lg" fullWidth leftSection={<IconCheck size={22} aria-hidden="true" />} onClick={() => navigate('/', { replace: true })}>
          Done
        </Button>
      </StickyActionBar>
    </div>
  );
}
